/**
 * MenuVisibilityProvider — top-level provider for menu-directory state.
 *
 * Historically the AppShell was the sole owner of this context: it
 * fetched `/api/menu-visibility` on mount and wrapped its own subtree
 * with a `<MenuVisibilityContext.Provider>`. That worked for header /
 * sidebar widgets rendered *inside* the shell but left `<MenuGate>`
 * dead-in-the-water because gates in App.js sit *outside* every
 * page's AppShell (each page renders its own shell). Without a
 * provider above them the gates only ever saw the default no-op
 * context and never redirected — a hidden `/dashboard` or `/deals`
 * would still load if the founder had a bookmark or was pushed there
 * by a login flow.
 *
 * This provider closes that gap. It:
 *   • Fetches `/menu-visibility` whenever the user becomes
 *     authenticated (and re-fetches on logout → login).
 *   • Warm-starts from the sessionStorage cache the AppShell already
 *     writes ("cpg.menu.layout") so the first render never flashes an
 *     inaccessible page before the fetch resolves.
 *   • Exposes the same shape the AppShell provider exposes so
 *     downstream consumers (MenuGate / AccountMenu / MenuFlag) are
 *     interchangeable — the AppShell keeps its own inner provider
 *     which naturally wins for anything rendered inside the shell.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MenuVisibilityContext } from "./MenuVisibilityContext";
import { useAuth } from "./AuthContext";
import { api } from "../lib/api";
import { subscribeMenuVisibility } from "../lib/menuVisibilityBus";

const CACHE_KEY = "cpg.menu.layout";

function readCache() {
  try {
    const raw = window.sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
}

export function MenuVisibilityProvider({ children }) {
  const { status } = useAuth();
  const [layout, setLayout] = useState(() => readCache() || {});

  // Extracted refetch so both the auth-transition effect below and the
  // "menu-directory changed" bus subscriber can call it identically.
  const refetch = useCallback(() => {
    return api.get("/menu-visibility").then((r) => {
      if (!r?.data) return;
      setLayout(r.data);
      try { window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(r.data)); } catch { /* noop */ }
    }).catch(() => { /* keep last known state — gate stays permissive */ });
  }, []);

  useEffect(() => {
    if (status !== "authenticated") return;
    let alive = true;
    refetch().then(() => { if (!alive) return; });
    return () => { alive = false; };
  }, [status, refetch]);

  // Live-refresh subscription. When an admin saves Menu Directory in
  // ANY open tab (or this one), the bus fires — clear the cache and
  // re-fetch so consumers see the new page_elements_disabled /
  // disabled / coming_soon state without a manual reload.
  useEffect(() => {
    if (status !== "authenticated") return undefined;
    return subscribeMenuVisibility(refetch);
  }, [status, refetch]);

  const value = useMemo(() => {
    const disabled = new Set(layout?.disabled || []);
    const comingSoon = new Set(layout?.coming_soon || []);
    const isNew = new Set(layout?.is_new || []);
    const pageElementsDisabled = new Set(layout?.page_elements_disabled || []);
    const planGated = new Set(layout?.plan_gated || []);
    // `children_allowed` is a per-parent LIST of FORBIDDEN child ids
    // (misleading name — see routers/menu_directory.py
    // `_plan_allowed_children`). Convert to Sets for O(1) lookup and
    // expose via `isChildPlanGated(parent, child)` so pages never have
    // to know about the wire shape.
    const childrenAllowed = {};
    for (const [parent, forbidden] of Object.entries(layout?.children_allowed || {})) {
      childrenAllowed[parent] = new Set(forbidden || []);
    }
    const childrenOverrides = layout?.children_overrides || {};
    return {
      disabled,
      comingSoon,
      isNew,
      pageElementsDisabled,
      planGated,
      childrenAllowed,
      childrenOverrides,
      isDisabled: (id) => disabled.has(id),
      isComingSoon: (id) => comingSoon.has(id),
      hasNewBadge: (id) => isNew.has(id),
      isPageElementHidden: (id) => pageElementsDisabled.has(id),
      isPlanGated: (id) => planGated.has(id),
      isChildPlanGated: (parent, child) =>
        !!(childrenAllowed[parent] && childrenAllowed[parent].has(child)),
    };
  }, [layout]);

  return (
    <MenuVisibilityContext.Provider value={value}>
      {children}
    </MenuVisibilityContext.Provider>
  );
}
